<template>
	<v-card>
		<v-toolbar flat dense>
			<v-toolbar-title class="subheading">Certification Types</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn v-if="enableEditing" @click="$emit('add')" color="primary" small> Add Certification Type</v-btn>
		</v-toolbar>

		<v-card-text class="text-xs-left">
			<v-text-field v-model="searchInput" placeholder="Search" prepend-inner-icon="search" hide-details dense />

			<v-data-table :headers="headers" :items="filteredTypes" :search="searchInput" dense :pagination.sync="pagination" :rows-per-page-items="rowsPerPageItems" class="elevation-0">
				<template slot="items" slot-scope="props">
					<tr>
						<td>
							<a href="#" @click.prevent.stop="$emit('edit', props.item)">{{ props.item.name }}</a>
							<v-chip v-if="props.item.isDeleted" color="red" text-color="white" small class="ml-2">Archived</v-chip>
						</td>
						<td>
							{{ props.item.description }}
						</td>
						<td class="">
							<v-icon v-if="hasReminders(props.item)" small class="mr-1" color="success">mdi-check-circle</v-icon>
							<v-icon v-else small class="mr-1" color="error">mdi-minus-circle</v-icon>
						</td>
						<td>
							<v-icon v-if="props.item.inUse" small class="mr-1" color="success">mdi-check-circle</v-icon>
							<v-icon v-else small class="mr-1" color="error">mdi-minus-circle</v-icon>
						</td>
						<td class="px-0">
							<v-menu offset-y :close-on-content-click="false">
								<template v-slot:activator="{ on }">
									<v-btn icon v-on="on"><v-icon>mdi-dots-horizontal</v-icon></v-btn>
								</template>
								<v-list>
									<v-list-tile @click.stop="$emit('edit', props.item)" v-if="enableEditing">
										<v-icon>mdi-pencil</v-icon>
										<v-list-tile-title class="ml-2">Edit</v-list-tile-title>
									</v-list-tile>
									<v-list-tile v-if="!props.item.inUse" @click="handleDeleteClick(props.item)">
										<v-icon>{{ props.item.isDeleted ? 'mdi-restore' : 'mdi-delete' }}</v-icon>
										<v-list-tile-title class="ml-2">Delete</v-list-tile-title>
									</v-list-tile>
								</v-list>
							</v-menu>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card-text>

		<confirmation-dialog
			:dialog="showToggleArchiveDialog"
			:title="archiveSelectedCert && archiveSelectedCert.isDeleted ? 'Unarchive Certification Type' : 'Delete Certification Type'"
			:text="archiveSelectedCert && archiveSelectedCert.isDeleted ? 'Are you sure you want to delete this certification type?' : 'Are you sure you want to delete this certification type?'"
			:is-confirming="isTogglingArchive"
			@onConfirm="confirmToggleArchive"
			@onCancel="showToggleArchiveDialog = false"
		/>
	</v-card>
</template>

<script>
	import { formatTimespan, sortRenewalReminders, stringifyInterval } from '@utils/timespanUtil';
	import CertificationTypeListFilterMenu from '@components/certifications/CertificationTypeListFilterMenu.vue';
	import { mapActions } from 'vuex';

	export default {
		name: 'CertificationsTypeList',
		components: { CertificationTypeListFilterMenu },
		props: {
			certificationTypes: Array,
			enableEditing: { type: Boolean, default: false }
		},
		data() {
			return {
				searchInput: '',
				hideDeleted: true,
				showToggleArchiveDialog: false,
				isTogglingArchive: false,
				archiveSelectedCert: null,
				pagination: {
					sortBy: 'name',
					rowsPerPage: 12
				},
				rowsPerPageItems: [12, 30, 60, { text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }]
			};
		},
		computed: {
			headers() {
				return [
					{ text: 'Name', value: 'name' },
					{ text: 'Description', value: 'description' },
					{ text: 'Sends Expiry Reminders', value: 'reminders' },
					{ text: 'In Use', value: 'inUse' },
					{ text: '', value: 'actions', sortable: false }
				];
			},
			filteredTypes() {
				let types = [...this.certificationTypes];
				if (this.hideDeleted) types = types.filter(c => !c.isDeleted);
				if (this.searchInput) types = types.filter(c => c.name.toLowerCase().includes(this.searchInput.toLowerCase()));
				return types.sort((a, b) => a.name.localeCompare(b.name));
			}
		},
		methods: {
			...mapActions({
				updateCertTypeArchive: 'certificationTypes/updateArchiveStatus'
			}),
			formatTimespan,
			getAllReminders(cert) {
				return sortRenewalReminders(cert.configuration.renewalReminders || []);
			},
			hasReminders(cert) {
				return cert.configuration.hasExpiry && cert.configuration.renewalReminders.length > 0;
			},
			handleDeleteClick(cert) {
				this.archiveSelectedCert = cert;
				this.showToggleArchiveDialog = true;
			},
			getModelToArchive() {
				return {
					...this.archiveSelectedCert,
					configuration: {
						...this.archiveSelectedCert.configuration,
						renewalReminders: (this.archiveSelectedCert.configuration.renewalReminders || []).map(r => ({
							interval: stringifyInterval(r.interval),
							reminderType: 'Upload'
						}))
					}
				};
			},
			async confirmToggleArchive() {
				const model = this.getModelToArchive();
				model.isDeleted = !this.archiveSelectedCert.isDeleted;
				this.isTogglingArchive = true;
				const result = await this.updateCertTypeArchive(model);
				if (result.isSuccess) this.showToggleArchiveDialog = false;
				this.isTogglingArchive = false;
			}
		}
	};
</script>

<style scoped></style>
